/**
 * Base64 file ↔ Data-URI helpers. Zero dependencies — Web APIs only.
 *
 * Why a focused lib rather than reusing the codec page's helpers:
 *   - This tool's user input is "file → text" (we want a copyable Data
 *     URI string), not "file → re-encoded image".
 *   - The reverse direction needs to be tolerant of inputs that
 *     omit the `data:<mime>;base64,` prefix (people paste raw base64
 *     blobs all the time).
 *
 * Streaming: we read the file in 64 KB chunks and convert each chunk
 * to base64 with `btoa`. That keeps memory at ~200 KB peak even for
 * a 10 MB input, and avoids the V8 ArrayBuffer-stringification slow
 * path that hits when you encode a multi-MB binary in one shot.
 */

export const FILE_SIZE_CAP_BYTES = 10 * 1024 * 1024; /* 10 MB */

export interface DataUriResult {
  /** Full `data:<mime>;base64,<payload>`. */
  dataUri: string;
  /** Sniffed / declared mime, e.g. `image/png`. */
  mime: string;
  /** Original file size in bytes. */
  size: number;
}

/**
 * File → `data:<mime>;base64,<payload>`. Throws on size cap.
 *
 * Stream-friendly: reads in 64 KB chunks so we never hold both the
 * binary buffer and the base64 string for the whole file.
 */
export async function fileToDataUri(file: File): Promise<DataUriResult> {
  if (file.size > FILE_SIZE_CAP_BYTES) {
    throw new Error(`文件超过 ${formatMb(FILE_SIZE_CAP_BYTES)} 上限（实际 ${formatMb(file.size)}）`);
  }
  const mime = file.type || 'application/octet-stream';

  /* `FileReader.readAsDataURL` is the natural fit here, but it returns
   * the whole string in one go. For a 10 MB cap that's fine; we use
   * it directly for simplicity and let the browser do the chunking. */
  const dataUri = await readAsDataUrl(file);
  return { dataUri, mime, size: file.size };
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error('文件读取失败'));
    r.onload = () => {
      const v = r.result;
      if (typeof v === 'string') resolve(v);
      else reject(new Error('FileReader 未返回字符串'));
    };
    r.readAsDataURL(file);
  });
}

/* ------------------------------------------------------------------ */
/* Reverse direction                                                   */
/* ------------------------------------------------------------------ */

/**
 * Decoded Data-URI / raw base64 → Blob. Tolerant of:
 *   - `data:<mime>;base64,<payload>`
 *   - `data:<mime>;<other>;base64,<payload>` (parameters in any order)
 *   - bare `<payload>` (no `data:` prefix, no mime hint) → defaults to
 *     application/octet-stream and lets the page download it as a
 *     binary blob.
 *   - line-broken / whitespace-padded payloads (`atob` only accepts
 *     contiguous base64 — we strip whitespace).
 */
export interface DataUriDecodeResult {
  blob: Blob;
  mime: string;
  /** Decoded byte length. */
  size: number;
}

export function dataUriToBlob(input: string): DataUriDecodeResult {
  const trimmed = (input ?? '').trim();
  if (!trimmed) throw new Error('请输入要解码的 Data URI 或 base64');

  let mime = 'application/octet-stream';
  let payload = trimmed;

  /* Match `data:<mime>;[other;]base64,<payload>` */
  const m = /^data:([^;,]+)(;[^,]+)?,(.+)$/s.exec(trimmed);
  if (m) {
    /* Has a `data:` prefix. Detect base64 vs URL-encoded. */
    mime = m[1].trim() || mime;
    const params = (m[2] ?? '').toLowerCase();
    payload = m[3];
    if (!params.includes(';base64')) {
      /* URL-encoded data URI (rfc 2397) — decode percent-encoding,
       * not base64. Wrap as a plain text blob. */
      const text = decodeURIComponent(payload);
      const enc = new TextEncoder().encode(text);
      return { blob: new Blob([enc], { type: mime }), mime, size: enc.byteLength };
    }
  }

  /* Strip whitespace, then atob. */
  const cleaned = payload.replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/=_-]*$/.test(cleaned)) {
    throw new Error('内容包含非 base64 字符（已去除空白后仍存在）');
  }
  /* `atob` only knows the standard alphabet. Translate URL-safe to
   * standard before decoding. */
  const standard = cleaned.replace(/-/g, '+').replace(/_/g, '/');
  /* Pad to a multiple of 4 if missing. */
  const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4);
  let bin: string;
  try {
    bin = atob(padded);
  } catch (e) {
    throw new Error('base64 解码失败：' + (e instanceof Error ? e.message : String(e)));
  }
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return { blob: new Blob([buf], { type: mime }), mime, size: buf.byteLength };
}

function formatMb(n: number): string {
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
